import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { ArticleHeading } from "@/lib/article-html";

/**
 * Collapsible «في هذا المقال» list above the reading body. Collapsed by
 * default so the article opens on its own words; the active heading still
 * tracks scrolling once expanded.
 */
export function ArticleToc({ headings }: { headings: ArticleHeading[] }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string | null>(headings[0]?.id ?? null);

  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 },
    );
    for (const h of headings) {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [headings]);

  if (headings.length < 2) return null;

  return (
    <nav aria-label="في هذا المقال" className="mt-8">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="article-toc-list"
        className="flex min-h-12 w-full items-center justify-between gap-3 text-start"
      >
        <span className="font-display text-[13px] font-bold text-amber-800">في هذا المقال</span>
        <span className="inline-flex items-center gap-1.5 font-display text-[14px] font-bold text-brown-700">
          {open ? "إخفاء" : "عرض"}
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-ds-base ${open ? "rotate-180" : ""}`}
            strokeWidth={1.75}
            aria-hidden
          />
        </span>
      </button>

      <div
        id="article-toc-list"
        className={`grid transition-all duration-ds-base ease-ds ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <ol className="min-h-0 divide-y divide-hairline-subtle overflow-hidden">
          {headings.map((h, i) => (
            <li key={h.id}>
              <a
                href={`#${h.id}`}
                tabIndex={open ? 0 : -1}
                aria-current={active === h.id ? "true" : undefined}
                className="flex items-center gap-4 py-3"
              >
                <span className="w-9 shrink-0 font-display text-[22px] font-extrabold text-amber-800">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className={`min-w-0 text-[15px] leading-relaxed ${
                    active === h.id ? "font-bold text-text-primary" : "text-text-secondary"
                  }`}
                >
                  {h.text}
                </span>
              </a>
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}
